import React, { useState, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextField,
} from '@statsbomb/kitbag-components';
import { SCOUT_TEMPLATE_MODES } from '../../Scout.constants';
import {
  SaveModalContent,
  SaveModalValidation,
  SaveModalValidationHeight,
} from '../SaveModal/SaveModal.styles';
import { isTemplateNameGood } from '../SaveModal/SaveModal.validator';
import { getScoutLoadModalTitle } from '../LoadModal/LoadModal.dataManipulation';
import { useStore } from '../../../../store/useStore';

// confirm modal for save, save-as, rename and delete
// can be called from the template management page or the load modal
const ScoutConfirmModal = forwardRef(
  (
    {
      modalId = 'scout-confirm-modal',
      mode = SCOUT_TEMPLATE_MODES.SAVE_AS,
      deleteFunction = null,
      saveFunction = null,
      saveAsNewFunction = null,
    },
    ref
  ) => {
    const templateName = useStore((s) => s.templateName);
    const setTemplateName = useStore((s) => s.setTemplateName);
    const savedScoutTemplates = useStore((s) => s.savedScoutTemplates);
    const activeTemplateId = useStore((s) => s.activeTemplateId);

    const [isOpen, setIsOpen] = useState(false);
    // validation for rename, save-as and quick-save template name input
    const { valid: validTemplateName, message: validatorMessage } =
      isTemplateNameGood(templateName, savedScoutTemplates, activeTemplateId);

    useImperativeHandle(ref, () => ({
      open() {
        setIsOpen(true);
      },
      close() {
        setIsOpen(false);
      },
    }));

    const getFunction = (modeIn) => {
      switch (modeIn) {
        case SCOUT_TEMPLATE_MODES.SAVE_AS:
          return saveAsNewFunction;
        case SCOUT_TEMPLATE_MODES.EDIT:
          return saveFunction;
        case SCOUT_TEMPLATE_MODES.DELETE:
          return deleteFunction;
        default:
          return () => {};
      }
    };

    return (
      <Modal
        id={`${modalId}`}
        isOpen={isOpen}
        onCancel={() => setIsOpen(false)}
        onEscape={() => setIsOpen(false)}
        testId="save-modal"
      >
        <ModalHeader id={`${modalId}`}>
          {getScoutLoadModalTitle(mode, templateName)}
        </ModalHeader>
        <ModalBody>
          {mode === SCOUT_TEMPLATE_MODES.DELETE && (
            <SaveModalContent>
              <p>
                You are about to permanently delete this filter set for all
                users on the account. This action can not be undone.
              </p>
              <br />
              <p>Are you sure you wish to proceed?</p>
            </SaveModalContent>
          )}
          {mode !== SCOUT_TEMPLATE_MODES.DELETE && (
            <SaveModalContent>
              <TextField
                id="scout-filter-set-name"
                labelPosition="none"
                value={templateName}
                onChange={(t) => setTemplateName(t.target.value)}
                placeholder="Filter set name"
              />
              <SaveModalValidationHeight>
                {!validTemplateName && (
                  <SaveModalValidation data-testid="modal-validation">
                    {validatorMessage}
                  </SaveModalValidation>
                )}
              </SaveModalValidationHeight>
            </SaveModalContent>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={getFunction(mode)}
            isDisabled={
              mode === SCOUT_TEMPLATE_MODES.DELETE ? false : !validTemplateName
            }
            variant={mode === SCOUT_TEMPLATE_MODES.DELETE ? 'error' : 'primary'}
          >
            {mode === SCOUT_TEMPLATE_MODES.DELETE ? 'Yes, Delete' : 'Save'}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
);

ScoutConfirmModal.propTypes = {
  modalId: PropTypes.string,
  mode: PropTypes.oneOf([
    SCOUT_TEMPLATE_MODES.SAVE_AS,
    SCOUT_TEMPLATE_MODES.EDIT,
    SCOUT_TEMPLATE_MODES.DELETE,
    SCOUT_TEMPLATE_MODES.QUICK,
  ]),
  selectedTemplate: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  deleteFunction: PropTypes.func,
  saveFunction: PropTypes.func,
  saveAsNewFunction: PropTypes.func,
};

export default ScoutConfirmModal;
